import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers, noString } from "./shared";

interface IntakeOutputFields {
  lineDescription: string;
  lineIntake: string;
  lineOutput: string;

  tubeDescription: string;
  tubeIntake: string;
  tubeOutput: string;

  airwayDescription: string;
  airwayIntake: string;
  airwayOutput: string;

  drainDescription: string;
  drainIntake: string;
  drainOutput: string;
}
export type IntakeOutputFormGroup = FormGroup<ControlsOf<IntakeOutputFields>>;

export const IntakeOutputAssessmentHelpers: AssessmentHelpers = {
  createFormGroup: createIntakeOutputFormGroup,
  toPDF: intakeOutputAssessmentToPDF,
  formToObject: formToIntakeOutputAssessment,
  initialState: {
    lineDescription: '',
    lineIntake: '',
    lineOutput: '',

    tubeDescription: '',
    tubeIntake: '',
    tubeOutput: '',

    airwayDescription: '',
    airwayIntake: '',
    airwayOutput: '',

    drainDescription: '',
    drainIntake: '',
    drainOutput: '',
  },
};

/**
 * Holds information about the patient's intake and output assessment.
 */
export class IntakeOutputAssessment {
  lineDescription: string;
  lineIntake: string;
  lineOutput: string;

  tubeDescription: string;
  tubeIntake: string;
  tubeOutput: string;

  airwayDescription: string;
  airwayIntake: string;
  airwayOutput: string;

  drainDescription: string;
  drainIntake: string;
  drainOutput: string;

  constructor(data: Partial<IntakeOutputFields>) {
    this.lineDescription = data.lineDescription ?? IntakeOutputAssessmentHelpers.initialState.lineDescription;
    this.lineIntake = data.lineIntake ?? IntakeOutputAssessmentHelpers.initialState.lineIntake;
    this.lineOutput = data.lineOutput ?? IntakeOutputAssessmentHelpers.initialState.lineOutput;

    this.tubeDescription = data.tubeDescription ?? IntakeOutputAssessmentHelpers.initialState.tubeDescription;
    this.tubeIntake = data.tubeIntake ?? IntakeOutputAssessmentHelpers.initialState.tubeIntake;
    this.tubeOutput = data.tubeOutput ?? IntakeOutputAssessmentHelpers.initialState.tubeOutput;

    this.airwayDescription = data.airwayDescription ?? IntakeOutputAssessmentHelpers.initialState.airwayDescription;
    this.airwayIntake = data.airwayIntake ?? IntakeOutputAssessmentHelpers.initialState.airwayIntake;
    this.airwayOutput = data.airwayOutput ?? IntakeOutputAssessmentHelpers.initialState.airwayOutput;

    this.drainDescription = data.drainDescription ?? IntakeOutputAssessmentHelpers.initialState.drainDescription;
    this.drainIntake = data.drainIntake ?? IntakeOutputAssessmentHelpers.initialState.drainIntake;
    this.drainOutput = data.drainOutput ?? IntakeOutputAssessmentHelpers.initialState.drainOutput;
  }
}

function createIntakeOutputFormGroup(intakeOutputAssessment?: IntakeOutputAssessment) {
  const data = intakeOutputAssessment || new IntakeOutputAssessment(IntakeOutputAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<IntakeOutputFields>>({
    lineDescription: new FormControl<string | null>(data.lineDescription),
    lineIntake: new FormControl<string | null>(data.lineIntake),
    lineOutput: new FormControl<string | null>(data.lineOutput),
    tubeDescription: new FormControl<string | null>(data.tubeDescription),
    tubeIntake: new FormControl<string | null>(data.tubeIntake),
    tubeOutput: new FormControl<string | null>(data.tubeOutput),
    airwayDescription: new FormControl<string | null>(data.airwayDescription),
    airwayIntake: new FormControl<string | null>(data.airwayIntake),
    airwayOutput: new FormControl<string | null>(data.airwayOutput),
    drainDescription: new FormControl<string | null>(data.drainDescription),
    drainIntake: new FormControl<string | null>(data.drainIntake),
    drainOutput: new FormControl<string | null>(data.drainOutput),
  });
}

function intakeOutputAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: IntakeOutputAssessment): void {
  pdf.addSectionTitle("Intake and Output");
  pdf.pdf.setFont("Helvetica", "normal");
  pdf.pdf.text("Line", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.lineDescription)
  pdf.addTwoColumnText("Intake", assessment.lineIntake, "Output", assessment.lineOutput);
  pdf.currY += 10;
  pdf.pdf.text("Tube", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.tubeDescription)
  pdf.addTwoColumnText("Intake", assessment.tubeIntake, "Output", assessment.tubeOutput);
  pdf.currY += 10;
  pdf.pdf.text("Airway", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.airwayDescription)
  pdf.addTwoColumnText("Intake", assessment.airwayIntake, "Output", assessment.airwayOutput);
  pdf.currY += 10;
  pdf.pdf.text("Drain", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Description", assessment.drainDescription)
  pdf.addTwoColumnText("Intake", assessment.drainIntake, "Output", assessment.drainOutput);
  pdf.currY += pdf.newSection;
}

function formToIntakeOutputAssessment(fg: IntakeOutputFormGroup) {
  return new IntakeOutputAssessment({
    lineDescription: fg.controls.lineDescription.value!,
    lineIntake: fg.controls.lineIntake.value!,
    lineOutput: fg.controls.lineOutput.value!,

    tubeDescription: fg.controls.tubeDescription.value!,
    tubeIntake: fg.controls.tubeIntake.value!,
    tubeOutput: fg.controls.tubeOutput.value!,

    airwayDescription: fg.controls.airwayDescription.value!,
    airwayIntake: fg.controls.airwayIntake.value!,
    airwayOutput: fg.controls.airwayOutput.value!,

    drainDescription: fg.controls.drainDescription.value!,
    drainIntake: fg.controls.drainIntake.value!,
    drainOutput: fg.controls.drainOutput.value!,
  });
}
